import { useEffect, useRef, useState } from 'react';
import { AnchoredPopover } from '../shared/AnchoredPopover';
import type { ClientStatusDetails } from '../../api/types';
import { REASON_CODE_LABEL, CALL_BACK_STATUS as CALL_BACK } from './clientStatusVocab';
import './ClientStatusControl.css';

const POP_CLASS = 'cs-status-menu';

export interface ClientStatusValue {
  status?: string | null;
  age_days?: number | null;
  is_expired?: boolean;
  expiry_reason?: string | null;
  user_email?: string | null;
  call_back_date?: string | null;
  reason_code?: string | null;
  reason_text?: string | null;
}

interface Props {
  value?: ClientStatusValue | null;
  options: string[];
  reasonCodes: string[];
  /** Statuses that take the client off the list for good, so the UI can say so. */
  permanentStatuses?: string[];
  onSet: (status: string, details: ClientStatusDetails) => void;
  onClear: () => void;
  /** `row` is the call list; `card` is the compact carousel form. */
  variant?: 'row' | 'card';
  disabled?: boolean;
}

function isoDaysFromNow(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Set a client status with an optional reason and note, mirroring the Sales View
 * close-out control so a rep working both boards uses one workflow.
 *
 * The menu MUST stay an `AnchoredPopover`: the carousel and the call list both
 * scroll, and an in-flow absolute menu is clipped by the scroll container with no
 * console error — and jsdom does not clip, so tests stay green while it is invisible.
 */
export function ClientStatusControl({
  value,
  options,
  reasonCodes,
  permanentStatuses = [],
  onSet,
  onClear,
  variant = 'row',
  disabled,
}: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [callBackDate, setCallBackDate] = useState('');

  // Seed the form from whatever is already recorded, so editing a status does not
  // silently blank the note a colleague left on it.
  useEffect(() => {
    if (!open) return;
    setReasonCode(value?.reason_code || '');
    setReasonText(value?.reason_text || '');
    setCallBackDate(value?.call_back_date || isoDaysFromNow(1));
  }, [open, value?.reason_code, value?.reason_text, value?.call_back_date]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      // Portalled: the menu is not a descendant of its anchor, so without this its
      // own clicks read as "outside" and close it instantly.
      if (t?.closest?.(`.${POP_CLASS}`)) return;
      if (wrapRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const act = (status: string) => {
    if (status === CALL_BACK && !callBackDate) return;
    onSet(status, {
      callBackDate: status === CALL_BACK ? callBackDate : null,
      reasonCode: reasonCode || null,
      reasonText: reasonText.trim() || null,
    });
    setOpen(false);
  };

  const current = value?.status;
  const permanent = new Set(permanentStatuses);

  return (
    <div
      className={`cs-status-control cs-status-control-${variant}`}
      ref={wrapRef}
      // Both hosts are themselves clickable (row opens the popout, card selects).
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={`cs-status-btn${current ? ' cs-status-btn-set' : ''}${
          value?.is_expired ? ' cs-status-btn-due' : ''
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title={
          current
            ? `${current}${value?.age_days != null ? ` · ${value.age_days}d ago` : ''}${
                value?.user_email ? ` · set by ${value.user_email}` : ''
              }`
            : 'Set status'
        }
      >
        {current || 'Set status'}
        {value?.is_expired && <span className="cs-status-due-dot" title="Due again" />}
        <span className="cs-status-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <AnchoredPopover
          anchorRef={wrapRef}
          align="right"
          className={POP_CLASS}
          role="menu"
          ariaLabel="Set client status"
        >
          <div className="cs-status-menu-list">
            {options.map((opt) => (
              <button
                key={opt}
                type="button"
                role="menuitem"
                className={`cs-status-menu-item${current === opt ? ' active' : ''}`}
                disabled={opt === CALL_BACK && !callBackDate}
                onClick={() => act(opt)}
              >
                <span className="cs-status-menu-label">{opt}</span>
                {permanent.has(opt) && (
                  <span className="cs-status-menu-hint">Never comes back on its own</span>
                )}
              </button>
            ))}
            {current && (
              <button
                type="button"
                role="menuitem"
                className="cs-status-menu-item cs-status-menu-clear"
                onClick={() => { setOpen(false); onClear(); }}
              >
                Clear status
              </button>
            )}
          </div>

          <div className="cs-status-menu-optional">
            <label className="cs-status-field">
              <span>Reason</span>
              <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
                <option value="">No reason</option>
                {reasonCodes.map((r) => (
                  <option key={r} value={r}>{REASON_CODE_LABEL[r] ?? r}</option>
                ))}
              </select>
            </label>

            <label className="cs-status-field">
              <span>Call back on</span>
              <input
                type="date"
                value={callBackDate}
                min={isoDaysFromNow(0)}
                onChange={(e) => setCallBackDate(e.target.value)}
              />
            </label>

            <label className="cs-status-field cs-status-field-wide">
              <span>Note</span>
              <textarea
                rows={2}
                maxLength={2000}
                placeholder="Optional"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
              />
            </label>
            <p className="cs-status-menu-note">
              Reason and note are optional. The date only applies to “{CALL_BACK}”.
            </p>
          </div>
        </AnchoredPopover>
      )}
    </div>
  );
}

export default ClientStatusControl;
